import React from 'react';

// ---------- 1. 组件注册表 ----------
// 所有需要在图中存储的 React 组件都必须在此注册
const componentRegistry = {};

/**
 * 注册一个可被序列化的 React 组件
 * @param {string} name 组件名称（序列化时使用的标识）
 * @param {React.ComponentType} component React 组件
 */
export function registerSerializableComponent(name, component) {
    componentRegistry[name] = component;
}

// ---------- 2. 序列化：React 元素 -> 普通对象 ----------
/**
 * 判断一个值是否为可序列化的 React 元素（有效元素且不是字符串/数字等）
 */
function isSerializableReactElement(value) {
    return React.isValidElement(value);
}

/**
 * 将 React 元素转换为可序列化的描述符
 * @param {React.ReactElement} element React 元素
 * @returns {Object} 描述符对象
 */
function serializeReactElement(element) {
    // 获取组件名称
    let componentName;
    const type = element.type;
    if (typeof type === 'string') {
        // 原生 DOM 标签，例如 'div', 'span'，存储特殊标记
        componentName = `__native__${type}`;
    } else {
        // 自定义组件，必须在注册表中存在
        componentName = type.displayName || type.name;
        if (!componentName) {
            console.warn('React component without displayName/name, using fallback');
            componentName = 'AnonymousComponent';
        }
        if (!componentRegistry[componentName]) {
            console.error(`Component "${componentName}" not registered in componentRegistry, will not be restored correctly.`);
        }
    }

    // 提取 props，过滤掉无法序列化的字段（如 ref）
    const { key, ref, ...serializableProps } = element.props;
    // 递归处理 props 中可能包含的其他 React 元素
    const processedProps = deepSerialize(serializableProps);

    return {
        __reactComponent: true,
        componentName: componentName,
        props: processedProps,
        key: key ?? null,
    };
}

/**
 * 深度遍历对象/数组，将所有 React 元素转换为描述符
 * @param {any} data
 * @returns {any}
 */
function deepSerialize(data) {
    if (data === null || typeof data !== 'object') {
        return data;
    }
    if (isSerializableReactElement(data)) {
        return serializeReactElement(data);
    }
    if (Array.isArray(data)) {
        return data.map(item => deepSerialize(item));
    }
    // 普通对象
    const result = {};
    for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
            result[key] = deepSerialize(data[key]);
        }
    }
    return result;
}

/**
 * 将整个 graph 的 JSON 数据序列化（清理 React 元素）
 * @param {import('@antv/x6').Graph} graph X6 Graph 实例
 * @returns {Object} 可安全 JSON.stringify 的普通对象
 */
export function serializeGraph(graph) {
    const json = graph.toJSON();
    return deepSerialize(json);
}

// ---------- 3. 反序列化：普通对象 -> React 元素 ----------
/**
 * 将描述符还原为 React 元素
 * @param {Object} descriptor 序列化时生成的描述符
 * @returns {React.ReactElement|any}
 */
function deserializeReactElement(descriptor) {
    if (!descriptor.__reactComponent) {
        return descriptor;
    }
    const { componentName, props, key } = descriptor;

    // 处理原生 DOM 标签
    if (componentName.startsWith('__native__')) {
        const tagName = componentName.slice('__native__'.length);
        return React.createElement(tagName, { ...props, key: key ?? undefined });
    }

    // 自定义组件
    const Component = componentRegistry[componentName];
    if (!Component) {
        console.error(`Unknown component: ${componentName}, fallback to null`);
        return null;
    }
    // 递归处理 props 中可能嵌套的描述符
    const restoredProps = deepDeserialize(props);
    return React.createElement(Component, { ...restoredProps, key: key ?? undefined });
}

/**
 * 深度遍历对象/数组，将所有描述符还原为 React 元素
 * @param {any} data
 * @returns {any}
 */
function deepDeserialize(data) {
    if (data === null || typeof data !== 'object') {
        return data;
    }
    // 如果是描述符对象（且包含 __reactComponent 标记）
    if (data.__reactComponent === true) {
        return deserializeReactElement(data);
    }
    if (Array.isArray(data)) {
        return data.map(item => deepDeserialize(item));
    }
    const result = {};
    for (const key in data) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
            result[key] = deepDeserialize(data[key]);
        }
    }
    return result;
}

/**
 * 将序列化后的 JSON 数据加载到 graph 中（还原 React 元素）
 * @param {import('@antv/x6').Graph} graph X6 Graph 实例
 * @param {Object} serializedData 由 serializeGraph 生成的对象
 */
export function deserializeGraph(graph, serializedData) {
    const restoredData = deepDeserialize(serializedData);
    graph.fromJSON(restoredData);
}